import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { WeightSlider } from '@/components/ui/Form/WeightSlider'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  GripVertical,
  Settings,
  TrendingUp,
  History,
  ChevronDown,
  ChevronRight,
  TestTube,
  Shield,
  Network,
  Brain,
  Activity
} from 'lucide-react'
import { FraudRule } from '@/services/mockRules'
import { cn } from '@/lib/utils'

interface RuleCardProps {
  rule: FraudRule
  onUpdate: (rule: FraudRule) => void
  onToggle: (ruleId: string) => void
}

export function RuleCard({ rule, onUpdate, onToggle }: RuleCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleWeightChange = (weight: number) => {
    onUpdate({ ...rule, weight })
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'pattern': return Shield
      case 'network': return Network
      case 'behavior': return Activity
      case 'ml': return Brain
      default: return Shield
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'pattern': return 'bg-blue-100 text-blue-800'
      case 'network': return 'bg-green-100 text-green-800'
      case 'behavior': return 'bg-purple-100 text-purple-800'
      case 'ml': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRiskLevel = (weight: number) => {
    if (weight >= 80) return { level: 'Critical', color: 'bg-risk-critical' }
    if (weight >= 60) return { level: 'High', color: 'bg-risk-high' }
    if (weight >= 40) return { level: 'Medium', color: 'bg-risk-medium' }
    return { level: 'Low', color: 'bg-risk-low' }
  }

  const CategoryIcon = getCategoryIcon(rule.category)
  const riskLevel = getRiskLevel(rule.weight)

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        "transition-all duration-200",
        isDragging && "shadow-lg scale-102 rotate-1",
        !rule.enabled && "opacity-60"
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className="p-1 hover:bg-muted rounded cursor-grab active:cursor-grabbing"
            >
              <GripVertical className="w-4 h-4 text-muted-foreground" />
            </button>

            {/* Rule Priority */}
            <div className="flex items-center justify-center w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded">
              {rule.priority}
            </div>

            {/* Rule Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <CategoryIcon className="w-4 h-4" />
                <h3 className="font-semibold">{rule.name}</h3>
                <Badge className={getCategoryColor(rule.category)}>
                  {rule.category}
                </Badge>
                {rule.testMode && (
                  <Badge variant="outline" className="text-xs">
                    <TestTube className="w-3 h-3 mr-1" />
                    Testing
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {rule.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Risk Level Badge */}
            <Badge className={cn("text-white", riskLevel.color)}>
              {riskLevel.level}
            </Badge>

            {/* Enable/Disable Switch */}
            <Switch
              checked={rule.enabled}
              onCheckedChange={() => onToggle(rule.id)}
            />

            {/* Expand Button */}
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>

        {/* Quick Metrics */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-green-600" />
            <span className="font-medium">{rule.metrics.accuracy}%</span>
            <span className="text-muted-foreground">accuracy</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium">{rule.metrics.detections}</span>
            <span className="text-muted-foreground">detections today</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium">{rule.metrics.falsePositives}</span>
            <span className="text-muted-foreground">false positives</span>
          </div>
        </div>
      </CardHeader>

      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Weight Slider */}
            <div>
              <WeightSlider
                value={rule.weight}
                onChange={handleWeightChange}
                label="Rule Weight"
                color="risk"
                showMarks
                disabled={!rule.enabled}
              />
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-lg font-bold text-primary">
                  {rule.metrics.dailyTriggers}
                </div>
                <div className="text-xs text-muted-foreground">
                  Daily Triggers
                </div>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-lg font-bold text-green-600">
                  {rule.metrics.accuracy}%
                </div>
                <div className="text-xs text-muted-foreground">
                  Accuracy Rate
                </div>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-lg font-bold text-red-600">
                  {rule.metrics.falsePositives}
                </div>
                <div className="text-xs text-muted-foreground">
                  False Positives
                </div>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-lg font-bold text-blue-600">
                  {Math.round((Date.now() - rule.metrics.lastTriggered.getTime()) / 60000)}m
                </div>
                <div className="text-xs text-muted-foreground">
                  Last Triggered
                </div>
              </div>
            </div>

            {/* Rule Configuration Preview */}
            <div className="p-4 bg-muted/20 rounded-lg">
              <h4 className="font-medium mb-2">Configuration</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(rule.config).slice(0, 4).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-muted-foreground capitalize">
                      {key.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                    </span>
                    <span className="font-medium">
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
              <Button variant="outline" size="sm">
                <TestTube className="w-4 h-4 mr-2" />
                Test Rule
              </Button>
              <Button variant="outline" size="sm">
                <History className="w-4 h-4 mr-2" />
                View History
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}